import { Canvas } from '@react-three/fiber';
import { Suspense, useEffect, useState } from 'react';
import { GameScene } from '@components/canvas/GameScene';
import { GameUI } from '@components/ui/GameUI';
import { LoadingScreen } from '@components/ui/LoadingScreen';
import { ConnectionStatus } from '@components/ui/ConnectionStatus';
import { useGameSession } from '@hooks/useGameSession';
import { useKeyboardShortcuts } from '@hooks/useKeyboardShortcuts';

// Declare the global function added in index.html
declare global {
  interface Window {
    hideSplashScreen?: () => void;
  }
}

function App(): React.JSX.Element {
  const [isReady, setIsReady] = useState(false);

  // Initialize game session (extracts token from URL and connects to RGS)
  const { isInitializing, error } = useGameSession();

  // Set up keyboard shortcuts (SPACE to cash out)
  useKeyboardShortcuts();

  useEffect(() => {
    // Hide the HTML splash screen once React is mounted and session is initializing
    // Add a small delay to ensure 3D scene starts loading
    const timer = setTimeout(() => {
      window.hideSplashScreen?.();
      setIsReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Show error if session initialization failed
  if (error && !isInitializing) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gradient-to-b from-aviator-skyDark to-aviator-sky">
        <div className="rounded-lg bg-aviator-danger/80 p-8 text-center">
          <h1 className="mb-4 text-2xl font-bold text-aviator-gold">Launch Error</h1>
          <p className="text-white">{error}</p>
          <p className="mt-4 text-sm text-white/60">
            Please launch the game from the platform.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-gradient-to-b from-aviator-skyDark to-aviator-sky">
      {(!isReady || isInitializing) && <LoadingScreen />}
      <Canvas
        shadows
        camera={{ position: [0, 10, 25], fov: 60 }}
        gl={{ antialias: true, alpha: false }}
      >
        <Suspense fallback={null}>
          <GameScene />
        </Suspense>
      </Canvas>
      <Suspense fallback={<LoadingScreen />}>
        <GameUI />
      </Suspense>
      <ConnectionStatus />
    </div>
  );
}

export default App;
